import './layout.css';

import { KeyIcon } from '@heroicons/react/24/outline';
import { isEmpty, isNumber } from 'radash';
import { memo, PropsWithChildren } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Icon } from '@/components/core/Icon';
import { Tooltip } from '@/components/core/Tooltip';
import { useCRUDView } from '@/hooks/useCRUDView';
import { useTranslation } from '@/hooks/useTranslation';
import { DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';

import { useGroupAttributeLabelContext } from '../Group/GroupAttributeLabelContext';
import { useAttributeLabelTranslator } from '../hooks/useAttributeLabelTranslator';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useValidationsStore, Validation } from '../hooks/useValidationsStore';
import { ClearButton } from './ClearButton';
import { EditorView } from './EditorView';
import { ValidationsBlock } from './ValidationBlock';

interface LayoutProps extends PropsWithChildren {
    attribute: DataModelAttribute;
    className?: string;
    direction?: 'vertical' | 'horizontal';
    fieldPath: string;
    gridMode: boolean;
    hideLabel?: boolean;
    isKeyAttribute?: boolean;
    beforeAttributeControl?: React.ReactNode;
    isClearable?: boolean;
    readonly? : boolean;
    labelClassName?: string;
    layoutClassName?: string
    /**
     * Controls how the label column aligns relative to the attribute control height
     * when `direction="horizontal"` (e.g. textarea should usually be top-aligned).
     */
    labelVerticalAlign?: 'center' | 'top';
    actionComponent?: React.ReactNode;
}

/*
 * A layout component that wraps a data model attribute and its validation, renders in two directions:
 *    horizontal: <label> <attribute>
 *    vertical: (as in Groups or Collections)
 *              <label>
 *              <attribute>
 *
 * TODO:
 * - Add HTML ids to allow the browser to scroll to a given attribute.
 */
const CoreLayout = ({
    attribute,
    className,
    children,
    direction = 'horizontal',
    fieldPath,
    gridMode,
    hideLabel,
    isKeyAttribute = false,
    beforeAttributeControl,
    isClearable = true,
    readonly = false,
    labelClassName = 'tw-block',
    labelVerticalAlign = 'center',
    actionComponent,
    layoutClassName
}: LayoutProps) => {

    const { onAddView, selectedView, onEditView } = useCRUDView();
    const groupLabelType = useGroupAttributeLabelContext();

    const useGroupEntryLabelSizing =
        groupLabelType === 'GroupEntry' ||
        attribute.typeName === DataModelAttributeTypeName.MultiReference ||
        attribute.typeName === DataModelAttributeTypeName.Collection ||
        attribute.typeName === DataModelAttributeTypeName.MultiDimensional;
    const groupTitle = 'tw-block tw-truncate';
    const labelSizingClassName = useGroupEntryLabelSizing ? groupTitle : labelClassName;

    const { t } = useTranslation();
    const isHorizontal = (direction === 'horizontal');

    const validationErrors = useValidationsStore(useShallow(state => state.validationErrors[fieldPath] as Validation[]));
    const localValidationErrors = useValidationsStore(useShallow(state => state.localValidationErrors[fieldPath] as Validation[]));
    const validationWarnings = useValidationsStore(useShallow(state => state.validationWarnings[fieldPath] as Validation[]));
    const hasErrors = !isEmpty(validationErrors);
    const hasWarnings = !isEmpty(validationWarnings);
    const hasValidations = !gridMode && (!isEmpty(validationErrors) || !isEmpty(validationWarnings) || !isEmpty(localValidationErrors));

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const getAttributeDescription = () => {
        const key = `attribute.${attribute.name}.description`;
        const translation = t(key);
        return translation !== key ? translation : undefined;
    };
    const tooltip = attribute.description || getAttributeDescription();

    const { translateAttributeLabel } = useAttributeLabelTranslator();
    let label =  undefined;
    if (!hideLabel) {
        label = translateAttributeLabel(attribute);
    }

    if (selectedView?.fields?.includes(attribute.name)) {
        return;
    }

    const showEditorView = Boolean(
        label &&
        !gridMode &&
        (onAddView || onEditView || isNumber(onEditView)),
    );

    // Only meaningful for horizontal layout where the label is in a separate column next to a potentially tall control (e.g. textarea).
    const horizontalLabelAlignClassName =
        labelVerticalAlign === 'top' ? 'tw-items-start' : 'tw-items-center';

    return (
        <>
            {isHorizontal ? (
                <div className="clear-button-container tw-flex tw-flex-col tw-gap-2">
                    <div className="tw-flex tw-flex-row tw-gap-3">
                        {showEditorView && (
                            <div className="tw-inline-block tw-w-10">
                                <EditorView attribute={attribute} />
                            </div>
                        )}

                        {label && (
                            <div
                                className={`tw-flex tw-flex-none tw-w-2/5 tw-min-w-0 tw-gap-2 tw-text-right tw-justify-end tw-shrink-0 ${
                                    horizontalLabelAlignClassName
                                } ${layoutClassName}`}
                            >
                                <label
                                    className={`tw-text-t-sm tw-m-0 tw-min-w-0 tw-text-t-secondary ${
                                        hasErrors ? 'tw-text-red-500' : hasWarnings ? 'tw-text-yellow-500' : ''
                                    } ${labelSizingClassName} tw-max-w-3/5`}
                                    title={label}
                                >
                                    {label}
                                </label>
                                {isKeyAttribute && (
                                    <span>
                                        <Icon
                                            icon={KeyIcon}
                                            size="sm"
                                            className=" tw-inline-block tw-w-10 tw-h-[1px] tw-text-gray-400"
                                        />
                                    </span>
                                )}
                                <span className="tw-flex tw-w-5 tw-h-5 tw-items-center tw-justify-center">
                                    {tooltip && <Tooltip popupContent={tooltip} />}
                                </span>
                            </div>
                        )}

                        <div className={`${label ? 'tw-flex-1 tw-min-w-0' : 'tw-w-full'} ${className || ''}`}>
                            {beforeAttributeControl}
                            <div className="tw-relative tw-w-full">
                                <div className="tw-w-full">{children}</div>
                                {isClearable && !readonly && (
                                    <div className="tw-absolute tw-top-0 tw-right-0 tw-mr-2.5 tw-mt-0">
                                        <ClearButton attributeName={attribute.name} fieldPath={fieldPath} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {hasValidations && (
                        <div className="tw-flex tw-flex-row tw-gap-3">
                            {showEditorView && <div className="tw-inline-block tw-w-10" aria-hidden="true" />}
                            {label && (
                                <div
                                    className="tw-flex-none tw-w-2/5 tw-min-w-0 tw-shrink-0"
                                    aria-hidden="true"
                                />
                            )}
                            <div className={`${label ? 'tw-flex-1 tw-min-w-0' : 'tw-w-full'}`}>
                                <ValidationsBlock attribute={attribute} fieldPath={fieldPath} gridMode={gridMode} />
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div
                    className={`tw-flex tw-flex-col tw-justify-center clear-button-container ${
                        useGroupEntryLabelSizing ? 'tw-gap-2' : 'tw-gap-3'
                    }`}
                >
                    {showEditorView && (
                        <div className="tw-inline-block tw-w-10">
                            <EditorView attribute={attribute} />
                        </div>
                    )}

                    {label && (
                        <div className="tw-flex tw-gap-2 tw-items-center tw-content-start tw-relative tw-size-full">
                            <label
                                className={`tw-text-t-sm tw-m-0 tw-min-w-0 tw-truncate tw-text-t-secondary ${
                                    hasErrors ? 'tw-text-red-500' : hasWarnings ? 'tw-text-yellow-500' : ''
                                } ${labelSizingClassName}`}
                                title={label}
                            >
                                {label}
                            </label>
                            {isKeyAttribute && (
                                <span>
                                    <Icon
                                        icon={KeyIcon}
                                        size="sm"
                                        className=" tw-inline-block tw-w-10 tw-h-[1px] tw-text-gray-400"
                                    />
                                </span>
                            )}
                            <span className="tw-flex tw-w-5 tw-h-5 tw-items-center tw-justify-center">
                                {tooltip && <Tooltip popupContent={tooltip} />}
                            </span>

                            {actionComponent && (
                                <div className="tw-ml-auto">{actionComponent}</div>
                            )}
                        </div>
                    )}

                    <div className={`${className || ''}`}>
                        {beforeAttributeControl}
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <div className="tw-relative tw-w-full">
                                <div className="tw-w-full">{children}</div>
                                {isClearable && !readonly && (
                                    <div className="tw-absolute tw-top-0 tw-right-0 tw-mr-2.5 tw-mt-0">
                                        <ClearButton attributeName={attribute.name} fieldPath={fieldPath} />
                                    </div>
                                )}
                            </div>
                            <ValidationsBlock attribute={attribute} fieldPath={fieldPath} gridMode={gridMode} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const Layout = memo(CoreLayout);

export { Layout };
